/**
 * Журнал (ADMIN_SPEC §3.9) — аудит платформенной зоны: кто из команды что
 * сделал. Лента с фильтром периода, группировкой по дням и cursor-пагинацией
 * («Показать ещё»). Данные — GET admin/audit.
 */

import {useState} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';

import {getAudit} from '@services/audit';
import {keys} from '@queries/keys';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';
import {cn} from '@lib/utils';

import {groupByDay, periodFrom, type PeriodKey, PERIODS} from './Audit.utils';
import {AuditRow} from './AuditRow';

export const Audit = () => {
  const [period, setPeriod] = useState<PeriodKey>('all');
  const from = periodFrom(period);

  const query = useInfiniteQuery({
    queryKey: keys.audit.list({from: from ?? 'all'}),
    queryFn: ({pageParam}) => getAudit({from, cursor: pageParam, limit: 20}),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = query.data?.pages.flatMap((p) => p.items) ?? [];
  const groups = groupByDay(entries);

  return (
    <>
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur max-md:px-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold -tracking-[.02em]">Журнал</h1>
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            Аудит платформенной зоны · действия команды
          </div>
        </div>
        <div className="flex rounded-[10px] border border-skr-line bg-card p-[3px]">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={cn(
                'rounded-[8px] px-3 py-1.5 text-[12.5px] font-medium text-muted-foreground transition',
                period === p.key && 'bg-skr-head font-semibold text-foreground',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-[1080px] px-[30px] pb-[60px] pt-[22px] max-md:px-4">
        {query.isLoading ? (
          <div className="flex min-h-[440px] items-center justify-center">
            <Loader label="Загружаем журнал…" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex min-h-[300px] items-center justify-center text-[13.5px] text-muted-foreground">
            Записей за период нет
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {groups.map((g) => (
              <section key={g.day}>
                <div className="mb-2 px-1 text-[11.5px] font-semibold uppercase tracking-[.05em] text-skr-label">
                  {g.day}
                </div>
                <div className="overflow-hidden rounded-[13px] border border-skr-line bg-card">
                  {g.items.map((e) => (
                    <AuditRow key={e.id} entry={e} />
                  ))}
                </div>
              </section>
            ))}
            {query.hasNextPage && (
              <Button
                variant="outline"
                className="h-11 self-center rounded-[10px] px-6"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
